import React, { useMemo, useState } from "react";
import { showAlert } from "../../../utils/showAlert";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { TOPICS, TopicId } from "../../../constants/topics";
import { updateRoom } from "../../../services/roomsService";
import type RootStackParamList from "../../../navigation/RootStackParamList";
import type { Room } from "../../../types/types";
import EditRoomView from "../ui/EditRoomView";
import { logEditRoom } from "../../../utils/analyticsUtils";
import {
  buildLocalDateTime,
  getMeetingTimeParts,
} from "../../../utils/meetingAvailability";
import { PREDEFINED_MEETING_NAMES } from "../../CreateRoom/ui/CreateRoomView.shared";

const topicIdSet = new Set<string>(TOPICS.map((topic) => topic.id));

type Props = NativeStackScreenProps<RootStackParamList, "EditMeeting">;

function extractTopics(room?: Room) {
  const topics = room?.topics ?? [];
  const normalized: TopicId[] = [];
  let custom = "";

  topics.forEach((topic) => {
    if (topicIdSet.has(topic)) {
      normalized.push(topic as TopicId);
    } else if (!custom) {
      custom = topic;
    }
  });

  return {
    normalized,
    custom,
  };
}

function getDateOrToday(value?: Date | string) {
  if (!value) {
    return new Date();
  }

  const date =
    value instanceof Date
      ? value
      : typeof value === "string" || typeof value === "number"
        ? new Date(value)
        : null;

  if (!date || Number.isNaN(date.getTime())) {
    return new Date();
  }

  return date;
}

export default function EditRoomContainer({ route, navigation }: Props) {
  const { room } = route.params;

  const { normalized, custom } = useMemo(() => extractTopics(room), [room]);
  const initialSlot = useMemo(
    () => getDateOrToday(room.startAt),
    [room.startAt],
  );
  const timeParts = getMeetingTimeParts(initialSlot);
  const [roomName, setRoomName] = useState<string>(room.name ?? "");
  const [selectedDate, setSelectedDate] = useState<Date>(() => initialSlot);
  const [selectedHour, setSelectedHour] = useState<string>(timeParts.hour);
  const [selectedMinute, setSelectedMinute] = useState<string>(
    timeParts.minute,
  );
  const [selectedAmPm, setSelectedAmPm] = useState<"AM" | "PM">(timeParts.ampm);
  const [selectedTopicIds, setSelectedTopicIds] =
    useState<TopicId[]>(normalized);
  const [customTopicText, setCustomTopicText] = useState<string>(custom);

  const initialMeetingNameOption = useMemo(() => {
    if (room.name && PREDEFINED_MEETING_NAMES.includes(room.name)) {
      return room.name;
    }
    return "Custom / Make your own...";
  }, [room.name]);

  const [selectedMeetingNameOption, setSelectedMeetingNameOption] =
    useState<string>(initialMeetingNameOption);
  const [maxParticipants, setMaxParticipants] = useState<number>(
    room.maxParticipants ?? 3,
  );

  const [roomNameError, setRoomNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const isAnythingSelected = selectedTopicIds.includes("anything");

  const toggleTopic = (id: TopicId) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id)
        ? prev.filter((topicId) => topicId !== id)
        : [...prev, id],
    );
  };

  const handleRoomNameChange = (text: string) => {
    setRoomName(text);
    if (roomNameError) {
      setRoomNameError(null);
    }
  };

  const handleSubmit = async () => {
    const finalRoomName =
      selectedMeetingNameOption === "Custom / Make your own..."
        ? roomName.trim()
        : selectedMeetingNameOption;

    if (!finalRoomName) {
      setRoomNameError("Meeting name is required");
      showAlert("Validation", "Please enter a meeting name.");
      return;
    }

    if (selectedTopicIds.length === 0) {
      showAlert("Validation", "Please select at least one topic.");
      return;
    }

    try {
      setIsSubmitting(true);
      const startAt = buildLocalDateTime(
        selectedDate,
        selectedHour,
        selectedMinute,
        selectedAmPm,
      );
      const startAtIso = startAt.toISOString();

      const response = await updateRoom(room.id, {
        name: finalRoomName,
        startAt: startAtIso,
        topics: selectedTopicIds,
        customTopicText: isAnythingSelected
          ? customTopicText.trim()
          : undefined,
        maxParticipants,
      });

      if (!response.success) {
        showAlert("Error", response.error ?? "Failed to update meeting.");
        return;
      }

      // Track which fields were changed
      const changedFields: string[] = [];
      if (room.name !== roomName.trim()) changedFields.push("name");
      if (room.startAt.toDateString() !== startAtIso)
        changedFields.push("startAt");
      if (room.topics?.join(",") !== selectedTopicIds.join(","))
        changedFields.push("topics");

      logEditRoom(room.id, changedFields);

      navigation.navigate("MeetingsTabs", { screen: "MyMeetings" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update meeting.";
      showAlert("Error", message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    (selectedMeetingNameOption !== "Custom / Make your own..." ||
      roomName.trim().length > 0) &&
    selectedTopicIds.length > 0;

  return (
    <EditRoomView
      roomName={roomName}
      roomNameError={roomNameError}
      selectedDate={selectedDate}
      selectedHour={selectedHour}
      selectedMinute={selectedMinute}
      selectedAmPm={selectedAmPm}
      selectedTopicIds={selectedTopicIds}
      customTopicText={customTopicText}
      isAnythingSelected={isAnythingSelected}
      isSubmitting={isSubmitting}
      canSubmit={canSubmit}
      currentTimezone={currentTimezone}
      onChangeRoomName={handleRoomNameChange}
      onChangeDate={setSelectedDate}
      onChangeHour={setSelectedHour}
      onChangeMinute={setSelectedMinute}
      onChangeAmPm={(value) => setSelectedAmPm(value as "AM" | "PM")}
      onToggleTopic={toggleTopic}
      onChangeCustomTopic={setCustomTopicText}
      onSubmit={handleSubmit}
      onCancel={() =>
        navigation.navigate("MeetingsTabs", { screen: "MyMeetings" })
      }
      selectedMeetingNameOption={selectedMeetingNameOption}
      onChangeSelectedMeetingNameOption={setSelectedMeetingNameOption}
      maxParticipants={maxParticipants}
      onChangeMaxParticipants={setMaxParticipants}
    />
  );
}
